import { useEffect, useState } from 'react';
import type { PanelPropio as Panel, UsuarioSesion } from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { fechaLarga } from '../componentes.js';
import { TareasDelDia } from '../componentes/TareasDelDia.js';

/**
 * EL PANEL DE QUIEN NO DIRIGE LA EMPRESA.
 *
 * Había un solo panel, el de Dirección, y el equipo veía el mismo: margen del
 * mes, coste soportado, facturable a clientes, gasto interno, rendimiento de
 * sus compañeros, facturas por cobrar. Eso es la cuenta de resultados de la
 * empresa puesta en la portada de todo el mundo.
 *
 * Este trae lo que a cada persona le sirve: lo que tiene que hacer hoy, lo que
 * lleva trabajado y gastado este mes, lo que le corresponde cobrar por ello, y
 * —si lleva casas— cómo están las suyas.
 */

interface Props {
  usuario: UsuarioSesion;
  idioma: Idioma;
  onAbrirIncidencia?: (incidenciaId: string, propiedadId: string) => void;
  /** Una tarea del CRM abre la ficha de su oportunidad. */
  onAbrirOportunidad?: (oportunidadId: string) => void;
  onAbrirProyecto?: (proyectoId: string) => void;
  onAbrirVisita?: (propiedadId: string) => void;
}

const MESES_CLAVE = [
  'mes.enero', 'mes.febrero', 'mes.marzo', 'mes.abril', 'mes.mayo', 'mes.junio',
  'mes.julio', 'mes.agosto', 'mes.septiembre', 'mes.octubre', 'mes.noviembre', 'mes.diciembre',
];

function Cifra({
  etiqueta,
  valor,
  pie,
  tono,
}: {
  etiqueta: string;
  valor: string;
  pie?: string;
  tono?: 'rojo';
}) {
  return (
    <div className="kpi">
      <span className="kpi-etiqueta">{etiqueta}</span>
      <b className={`kpi-valor${tono ? ` ${tono}` : ''}`}>{valor}</b>
      {pie && <span className="kpi-pie">{pie}</span>}
    </div>
  );
}

export function PanelPropio({
  usuario,
  idioma,
  onAbrirIncidencia,
  onAbrirOportunidad,
  onAbrirProyecto,
  onAbrirVisita,
}: Props) {
  const [panel, setPanel] = useState<Panel | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .panelPropio()
      .then((r) => setPanel(r.panel))
      .catch(() => setError(t(idioma, 'error.servidor')));
  }, [idioma]);

  const dinero = (cents: number) => formatearImporteDesdeCents(cents, idioma);
  const horas = (minutos: number) => `${Math.round((minutos / 60) * 10) / 10} h`;

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'panel.hola', { nombre: usuario.nombre.split(/\s+/)[0] ?? '' })}</h1>
          <p>
            {panel ? `${t(idioma, MESES_CLAVE[panel.mes - 1]!)} ${panel.anio} · ` : ''}
            {usuario.tenantNombre}
          </p>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* Lo primero de la mañana es lo que toca hoy, no las cifras del mes. */}
      <TareasDelDia
        idioma={idioma}
        {...(onAbrirProyecto ? { onAbrirProyecto } : {})}
        {...(onAbrirVisita ? { onAbrirVisita } : {})}
        {...(onAbrirIncidencia ? { onAbrirIncidencia } : {})}
        {...(onAbrirOportunidad ? { onAbrirOportunidad } : {})}
      />

      {panel && (
        <>
          {/*
            LO SUYO DE ESTE MES. Tres cifras y ninguna de la empresa: lo que ha
            trabajado, lo que ha adelantado de su bolsillo y lo que le
            corresponde cobrar por esas horas.
          */}
          <div className="rejilla kpis">
            <Cifra
              etiqueta={t(idioma, 'mio.horas')}
              valor={horas(panel.horasMinutos)}
              pie={t(idioma, 'mio.horasPie', { total: panel.partesDeTrabajo })}
            />
            <Cifra
              etiqueta={t(idioma, 'mio.gastos')}
              valor={dinero(panel.gastosCents)}
              pie={t(idioma, 'mio.gastosPie', { total: panel.gastosRegistros })}
            />
            <Cifra
              etiqueta={t(idioma, 'mio.aCobrar')}
              valor={dinero(panel.aCobrarCents)}
              pie={t(idioma, 'mio.aCobrarPie')}
            />
          </div>

          {/*
            Y LO DE SUS CASAS. Solo para quien lleva alguna: a quien no le han
            asignado ninguna, estas tres tarjetas dirian cero y no significarian
            nada.
          */}
          {panel.ocupacion.total > 0 && (
            <>
              <div className="rejilla kpis">
                <Cifra
                  etiqueta={t(idioma, 'mio.incidencias')}
                  valor={String(panel.incidencias.total)}
                  pie={t(idioma, 'mio.incidenciasPie', {
                    n1: panel.incidencias.n1,
                    n2: panel.incidencias.n2,
                  })}
                  {...(panel.incidencias.n1 + panel.incidencias.n2 > 0
                    ? { tono: 'rojo' as const }
                    : {})}
                />
                <Cifra
                  etiqueta={t(idioma, 'mio.ocupacion')}
                  valor={`${panel.ocupacion.ocupadas} / ${panel.ocupacion.total}`}
                  pie={t(idioma, 'mio.ocupacionPie')}
                />
              </div>

              <section className="tarjeta">
                <div className="tarjeta-cabeza">
                  <h2>{t(idioma, 'mio.proximasVisitas')}</h2>
                </div>
                {panel.proximasVisitas.length === 0 ? (
                  <p className="suave">{t(idioma, 'mio.sinVisitas')}</p>
                ) : (
                  <div className="lista">
                    {panel.proximasVisitas.map((v) => (
                      <div key={v.id} className="fila">
                        <span className="hora numerico">{v.hora ?? '—'}</span>
                        <span className="crecer">
                          {v.propiedadId && onAbrirVisita ? (
                            <button
                              type="button"
                              className="enlace tarea-titulo"
                              onClick={() => onAbrirVisita(v.propiedadId!)}
                            >
                              {v.titulo}
                            </button>
                          ) : (
                            <b>{v.titulo}</b>
                          )}
                          <small>
                            {v.propiedadNombre ?? t(idioma, 'dia.sinPropiedad')} ·{' '}
                            {fechaLarga(v.fecha, idioma)}
                          </small>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}

          {/*
            El informe de su mes. Es lo unico «financiero» que ve, y es suyo: sus
            horas, sus visitas, lo que ha resuelto y lo que le toca cobrar.
          */}
          <section className="tarjeta">
            <div className="tarjeta-cabeza">
              <h2>{t(idioma, 'mio.informe')}</h2>
              <a
                className="enlace"
                href={api.urlInformePersona(usuario.id, panel.anio, panel.mes)}
                target="_blank"
                rel="noreferrer"
              >
                {t(idioma, 'ia.descargar')}
              </a>
            </div>
            <p className="suave">{t(idioma, 'mio.informeNota')}</p>
          </section>
        </>
      )}
    </main>
  );
}
